import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

export const REPORT_TYPES = [
  { value: "inappropriate_behavior", label: "Inappropriate Behavior" },
  { value: "consent_boundary_issue", label: "Consent / Boundary Issue" },
  { value: "harassment", label: "Harassment" },
  { value: "photographer_conduct", label: "Photographer Conduct" },
  { value: "participant_conduct", label: "Participant Conduct" },
  { value: "no_show", label: "No Show" },
  { value: "unsafe_location", label: "Unsafe Location" },
  { value: "discrimination", label: "Discrimination" },
  { value: "payment_scam_concern", label: "Payment / Scam Concern" },
  { value: "other", label: "Other" },
];

/** Lets a Stranger or Photographer flag a safety concern about a specific booking,
 * or a general concern from the dashboard (optionally tied to one of their bookings).
 * Never asks the reporter to identify a match partner by name/id — the backend
 * resolves that internally. Goes to /admin/safety for investigation only. */
export const ReportConcernModal = ({ bookingId, bookings, onClose }) => {
  const [reportType, setReportType] = useState("");
  const [description, setDescription] = useState("");
  const [incidentDatetime, setIncidentDatetime] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState("");

  const showBookingPicker = !bookingId && Array.isArray(bookings) && bookings.length > 0;
  const effectiveBookingId = bookingId || (selectedBookingId !== "none" ? selectedBookingId : null) || null;

  const submit = async () => {
    setSubmitting(true);
    try {
      let evidence_urls = [];
      if (files.length) {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        const { data } = await api.post("/safety/reports/evidence", formData, { headers: { "Content-Type": "multipart/form-data" } });
        evidence_urls = data.evidence_urls;
      }
      await api.post("/safety/reports", {
        report_type: reportType,
        description,
        booking_id: effectiveBookingId || undefined,
        incident_datetime: incidentDatetime || null,
        evidence_urls,
      });
      setSubmitted(true);
      toast.success("Report submitted — our team will review it.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="report-concern-modal">
        <DialogHeader>
          <DialogTitle className="font-extrabold uppercase">Report a Concern</DialogTitle>
        </DialogHeader>
        {submitted ? (
          <p className="text-sm font-medium text-midnight-black/70" data-testid="report-submitted-message">
            Thank you for speaking up. Our safety team will review this and follow up if needed. Your identity is kept confidential from other participants.
          </p>
        ) : (
          <div className="space-y-3">
            {showBookingPicker && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Related booking (optional)</label>
                <Select value={selectedBookingId} onValueChange={setSelectedBookingId}>
                  <SelectTrigger data-testid="report-booking-select"><SelectValue placeholder="Not tied to a specific booking" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not tied to a specific booking</SelectItem>
                    {bookings.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.session?.title || "Session"} — {b.session?.session_date ? new Date(b.session.session_date).toLocaleDateString() : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger data-testid="report-type-select"><SelectValue placeholder="What happened?" /></SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="report-description-input" placeholder="Describe what happened in as much detail as you're comfortable sharing" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
            <Input data-testid="report-incident-datetime-input" type="datetime-local" value={incidentDatetime} onChange={(e) => setIncidentDatetime(e.target.value)} />
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Evidence (optional)</label>
              <Input data-testid="report-evidence-input" type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </div>
          </div>
        )}
        <DialogFooter>
          {submitted ? (
            <Button data-testid="report-close-button" onClick={onClose} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">CLOSE</Button>
          ) : (
            <>
              <Button variant="outline" data-testid="report-cancel-button" onClick={onClose} className="rounded-full font-bold">Cancel</Button>
              <Button data-testid="report-submit-button" disabled={submitting || !reportType || !description.trim()} onClick={submit} className="bg-red-600 hover:bg-red-700 text-clean-white font-bold rounded-full">
                {submitting ? "Submitting..." : "SUBMIT REPORT"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
